import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudioContext } from '@/contexts/AudioContext';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import MobilePlayer from '@/components/mobile/MobilePlayer';
import MobilePlaylist from '@/components/mobile/MobilePlaylist';
import MobileVisualizer from '@/components/mobile/MobileVisualizer';
import { Button } from '@/components/ui/button';
import { List, RotateCcw } from 'lucide-react';

// Sonic Groove Logo Component (matching desktop)
function SonicGrooveLogo() {
  return (
    <div className="relative w-10 h-10 bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 rounded-xl p-2 shadow-lg">
      <div className="absolute inset-1 flex items-center justify-center">
        <div className="flex items-end gap-0.5 h-6">
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [8, 16, 12, 20, 8] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [12, 8, 20, 10, 16] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [16, 20, 8, 16, 12] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [10, 12, 16, 8, 20] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [20, 8, 12, 16, 10] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          />
        </div>
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 via-blue-400/20 to-cyan-400/20 rounded-xl blur-sm"></div>
    </div>
  );
}

export function MobileLayout() {
  const audioContext = useAudioContext();
  const { orientation, isTablet, isMobile } = useDeviceDetection();
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  // Destructure with fallbacks
  const {
    analyserRef,
    dataArrayRef,
    currentSong,
    isPlaying = false,
    currentTime = 0,
    duration = 0,
    volume = 0.7,
    isLooping = false,
    isPlaylistLooping = false,
    seek = () => {},
    setVolume = () => {},
    toggleLoop = () => {},
    togglePlaylistLoop = () => {},
    playlist = [],
    currentIndex = 0,
    removeSong = () => {},
    reorderSongs = () => {},
    playNext = () => {},
    playPrevious = () => {},
    playSongAtIndex = () => {},
    addSongs = () => {},
    handlePlayPause = () => {},
    visualizerType = 'bars',
    setVisualizerType = () => {},
  } = audioContext || {};

  // Check if app is already installed
  React.useEffect(() => {
    // Check if running as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        window.navigator.standalone === true;
    setIsInstalled(isStandalone);
  }, []);

  // Capture the PWA install prompt
  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the default browser install prompt
      e.preventDefault();
      // Save the event for later use
      setDeferredPrompt(e);
      console.log('[PWA] Install prompt captured');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Handle logo click to trigger PWA install
  const handleLogoClick = async () => {
    if (isInstalled) {
      // Already installed, maybe show a message
      console.log('[PWA] App is already installed');
      return;
    }

    if (!deferredPrompt) {
      // No install prompt available
      console.log('[PWA] Install prompt not available. Use browser menu to install.');
      // Could show a toast message here
      alert('To install: Use your browser\'s "Add to Home Screen" or "Install App" option from the menu.');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user's response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`[PWA] User response: ${outcome}`);
    
    if (outcome === 'accepted') {
      console.log('[PWA] User accepted the install prompt');
      setIsInstalled(true);
    }
    
    // Clear the prompt as it can only be used once
    setDeferredPrompt(null);
  };

  console.log('MobileLayout render:', { 
    hasAudioContext: !!audioContext, 
    playlistLength: playlist?.length,
    currentSong: currentSong?.name,
    canInstall: !!deferredPrompt,
    isInstalled,
    isTablet,
    isMobile,
    orientation
  });

  // Determine if landscape warning should be shown (only for phones, not tablets)
  const showLandscapeWarning = orientation === 'landscape' && isMobile && !isTablet;
  
  // Tablet landscape layout
  if (isTablet && orientation === 'landscape') {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white flex flex-col overflow-hidden"
           style={{ minHeight: '100vh', minHeight: '100dvh' }}>
        {/* Tablet Landscape Header */}
        <div className="flex-none bg-black/20 backdrop-blur-sm border-b border-white/10">
          <div className="flex items-center justify-between px-6 py-3">
            <button 
              onClick={handleLogoClick}
              className="flex items-center gap-3 group active:scale-95 transition-transform touch-manipulation"
              aria-label={isInstalled ? "App installed" : deferredPrompt ? "Install as app" : "Sonic Groove"}
            >
              <div className="relative">
                <SonicGrooveLogo />
                {!isInstalled && deferredPrompt && (
                  <motion.div 
                    className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"
                    animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                )}
                {isInstalled && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              <div>
                <h1 className="text-xl font-bold text-white group-active:text-blue-300 transition-colors">
                  Sonic Groove
                </h1>
                {!isInstalled && deferredPrompt && (
                  <p className="text-xs text-green-400 font-medium">Tap to install</p>
                )}
              </div>
            </button>
            
            <Button
              variant="ghost"
              size="icon"
              className="text-white h-10 w-10"
              onClick={() => setShowPlaylist(true)}
            >
              <List className="h-6 w-6" />
            </Button>
          </div>
        </div>

        {/* Tablet Landscape Content - Side by side layout */}
        <div className="flex-1 flex overflow-hidden">
          {/* Visualizer - Left side (60% width) */}
          <div className="flex-[3] relative min-h-0">
            <MobileVisualizer
              analyserRef={analyserRef}
              dataArrayRef={dataArrayRef}
              isPlaying={isPlaying}
              visualizerType={visualizerType}
              setVisualizerType={setVisualizerType}
              currentSong={currentSong}
              currentTime={currentTime}
              orientation={orientation}
            />
          </div>

          {/* Player controls - Right side (40% width) */}
          <div className="flex-[2] flex flex-col bg-black/20 backdrop-blur-sm border-l border-white/10">
            <MobilePlayer
              currentSong={currentSong}
              isPlaying={isPlaying}
              currentTime={currentTime}
              duration={duration}
              volume={volume}
              isLooping={isLooping}
              isPlaylistLooping={isPlaylistLooping}
              handlePlayPause={handlePlayPause}
              onSeek={seek}
              onVolumeChange={setVolume}
              onToggleLoop={toggleLoop}
              onTogglePlaylistLoop={togglePlaylistLoop}
              onNext={playNext}
              onPrevious={playPrevious}
              orientation={orientation}
            />
          </div>
        </div>

        {/* Mobile Playlist - Full screen overlay */}
        <AnimatePresence>
          {showPlaylist && (
            <MobilePlaylist
              isOpen={showPlaylist}
              onClose={() => setShowPlaylist(false)}
              playlist={playlist}
              currentIndex={currentIndex}
              isPlaying={isPlaying}
              onFileUpload={addSongs}
              playSongAtIndex={playSongAtIndex}
              removeSong={removeSong}
              reorderSongs={reorderSongs}
              handlePlayPause={handlePlayPause}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Portrait layout (default for phones and tablets in portrait)

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white flex flex-col overflow-hidden"
         style={{ minHeight: '100vh', minHeight: '100dvh' }}>
      {/* Landscape orientation warning overlay - ONLY for phones, not tablets */}
      {showLandscapeWarning && (
        <div className="fixed inset-0 bg-black/95 z-[60] flex flex-col items-center justify-center p-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <RotateCcw className="h-10 w-10 text-white animate-pulse" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Please Rotate Device</h2>
            <p className="text-white/60 text-base leading-relaxed max-w-sm">
              For the best experience, please use portrait orientation
            </p>
          </motion.div>
        </div>
      )}
      {/* Mobile Header - Minimal */}
      <div className="flex-none bg-black/20 backdrop-blur-sm border-b border-white/10 safe-area-inset-top">
        <div className="flex items-center justify-between px-4 py-3">
          <button 
            onClick={handleLogoClick}
            className="flex items-center gap-3 group active:scale-95 transition-transform touch-manipulation"
            aria-label={isInstalled ? "App installed" : deferredPrompt ? "Install as app" : "Sonic Groove"}
          >
            <div className="relative">
              <SonicGrooveLogo />
              {/* Install indicator - show pulse if installable */}
              {!isInstalled && deferredPrompt && (
                <motion.div 
                  className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full"
                  animate={{ scale: [1, 1.2, 1], opacity: [1, 0.7, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
              {/* Checkmark if installed */}
              {isInstalled && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold text-white group-active:text-blue-300 transition-colors">
                Sonic Groove
              </h1>
              {!isInstalled && deferredPrompt && (
                <p className="text-xs text-green-400 font-medium">Tap to install</p>
              )}
            </div>
          </button>
          
          <Button
            variant="ghost"
            size="icon"
            className="text-white h-10 w-10"
            onClick={() => setShowPlaylist(true)}
          >
            <List className="h-6 w-6" />
          </Button>
        </div>
      </div>

      {/* Visualizer Area - Flexible */}
      <div className="flex-1 relative min-h-0 overflow-hidden">
        <MobileVisualizer
          analyserRef={analyserRef}
          dataArrayRef={dataArrayRef}
          isPlaying={isPlaying}
          visualizerType={visualizerType}
          setVisualizerType={setVisualizerType}
          currentSong={currentSong}
          currentTime={currentTime}
          orientation={orientation}
        />
      </div>

      {/* Mobile Player - Fixed at bottom */}
      <div className="flex-none" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <MobilePlayer
          currentSong={currentSong}
          isPlaying={isPlaying}
          currentTime={currentTime}
          duration={duration}
          volume={volume}
          isLooping={isLooping}
          isPlaylistLooping={isPlaylistLooping}
          handlePlayPause={handlePlayPause}
          onSeek={seek}
          onVolumeChange={setVolume}
          onToggleLoop={toggleLoop}
          onTogglePlaylistLoop={togglePlaylistLoop}
          onNext={playNext}
          onPrevious={playPrevious}
          orientation={orientation}
        />
      </div>

      {/* Mobile Playlist - Full screen overlay */}
      <AnimatePresence>
        {showPlaylist && (
          <MobilePlaylist
            isOpen={showPlaylist}
            onClose={() => setShowPlaylist(false)}
            playlist={playlist}
            currentIndex={currentIndex}
            isPlaying={isPlaying}
            onFileUpload={addSongs}
            playSongAtIndex={playSongAtIndex}
            removeSong={removeSong}
            reorderSongs={reorderSongs}
            handlePlayPause={handlePlayPause}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
